/**
 * readingTime — walk a case study's content tree and produce a rough "N min read"
 * estimate. Handles the block schema used by RichBlocks (`section` paragraph,
 * `grid-cards` items, `tabs`/`sub-tabs`/`accordion` nested blocks, `quotes`,
 * `persona`, `phase-hero`, and freeform strings) plus the legacy schemas
 * (`gallery`, `external`, `protected`, `note`).
 *
 * Uses 220 words / minute (an average reading pace for design case studies —
 * dense-content pages skew a little slower than pure prose).
 */

const WPM = 220;

const countStringWords = (s) => {
    if (typeof s !== 'string' || !s.trim()) return 0;
    // strip common markdown/HTML noise, then split on whitespace
    return s
        .replace(/<[^>]+>/g, ' ')
        .split(/\s+/)
        .filter(Boolean).length;
};

const countAny = (val) => {
    if (val == null) return 0;
    if (typeof val === 'string') return countStringWords(val);
    if (Array.isArray(val)) return val.reduce((sum, v) => sum + countAny(v), 0);
    if (typeof val === 'object') {
        // Skip URL-ish / image-source fields — they aren't reading material.
        // We only walk the fields that carry actual copy.
        const skipKeys = new Set([
            'src', 'href', 'url', 'image', 'cover', 'thumb', 'prototype',
            'sourceUrl', 'slug', 'no', 'category', 'kind', 'type', 'icon',
        ]);
        let n = 0;
        for (const [k, v] of Object.entries(val)) {
            if (skipKeys.has(k)) continue;
            n += countAny(v);
        }
        return n;
    }
    return 0;
};

export const estimateReadingMinutes = (caseStudy) => {
    if (!caseStudy || typeof caseStudy !== 'object') return null;
    // Hero-level copy
    let words = 0;
    words += countStringWords(caseStudy.title);
    words += countStringWords(caseStudy.subtitle);
    words += countStringWords(caseStudy.overview);
    if (Array.isArray(caseStudy.responsibilities)) {
        words += caseStudy.responsibilities.reduce(
            (n, r) => n + countStringWords(r),
            0
        );
    }
    // Body
    if (Array.isArray(caseStudy.blocks)) {
        words += countAny(caseStudy.blocks);
    }
    // Legacy gallery / note formats
    if (Array.isArray(caseStudy.captions)) {
        words += caseStudy.captions.reduce((n, c) => n + countStringWords(c), 0);
    }
    if (typeof caseStudy.note === 'string') {
        words += countStringWords(caseStudy.note);
    }

    if (words < 20) return null; // don't decorate placeholder pages
    return Math.max(1, Math.round(words / WPM));
};

export default estimateReadingMinutes;
